export type WizardCampaignType = 'stamp' | 'progress' | 'quest' | 'game' | 'instant_outcome';

export type WizardObjective = 'sales' | 'tasks' | 'habit' | 'upsell';

export type TriggerKind = 'transaction' | 'loyalty' | 'milestone' | 'reward' | 'others';

export type WizardGameType =
  | 'shake_the_tree'
  | 'hit_the_pinata'
  | 'scratch_card'
  | 'plinko'
  | 'spin_the_wheel';

/** The campaign activities a trigger may follow; the two that need a second picker are left out. */
export type CampaignActivity =
  | 'game_redeemed'
  | 'game_try_issued'
  | 'campaign_enrolled'
  | 'campaign_completed'
  | 'campaign_not_completed'
  | 'quest_task_completed';

export type LimitPeriod = 'day' | 'week' | 'month' | 'year';

export type WizardStep =
  | 'type'
  | 'objective'
  | 'action'
  | 'trigger'
  | 'tasks'
  | 'milestones'
  | 'rules'
  | 'game'
  | 'wedges'
  | 'wedgeOutcomes'
  | 'outcomes'
  | 'probability'
  | 'prizes'
  | 'limits'
  | 'review';

export interface PickedItem {
  id: number;
  name: string;
}

export interface WizardProperty {
  key: string;
  operator: 'equals' | 'not_equals';
  value: string;
}

export interface WizardTrigger {
  kind: TriggerKind;
  amountOn: boolean;
  amount: string;
  typeOn: boolean;
  transactionType: string | null;
  scheduleOn: boolean;
  days: string[];
  months: string[];
  deadline: string | null;
  propertiesOn: boolean;
  properties: WizardProperty[];
  /** Points trigger: the frames draw no programme picker, so the first programme is used. */
  loyalty: PickedItem | null;
  activity: CampaignActivity;
  campaign: PickedItem | null;
  rewardActivity: 'any' | 'specific';
  reward: PickedItem | null;
  appTrigger: PickedItem | null;
  /** Progress only: how the rule earns campaign points. */
  earnType: 'fixed' | 'variable';
  points: string;
  base: string;
  cap: string;
}

export interface WizardPrize {
  type: 'points' | 'reward';
  points: string;
  loyalty: PickedItem | null;
  reward: PickedItem | null;
}

/** A prize of the first steps' list; only a Stamp card reads its slot. */
export interface StampPrize extends WizardPrize {
  slot: number;
}

export interface GameOutcome extends WizardPrize {
  probability: string;
}

/** A Spin the Wheel wedge and what landing on it gives (6985:13262). */
export interface WizardWedge {
  type: 'points' | 'reward' | 'none';
  points: string;
  loyalty: PickedItem | null;
  reward: PickedItem | null;
}

export interface ProgressLevel {
  points: string;
  prizes: WizardPrize[];
}

export interface WizardLimits {
  totalOn: boolean;
  total: string;
  perUserOn: boolean;
  perUser: string;
  rateOn: boolean;
  rate: string;
  ratePeriod: LimitPeriod;
  userRateOn: boolean;
  userRate: string;
  userRatePeriod: LimitPeriod;
  autoEnrol: boolean;
  enrolByOn: boolean;
  enrolBy: string | null;
  windowOn: boolean;
  windowDays: string;
  start: string | null;
  endOn: boolean;
  end: string | null;
  autoIssue: boolean;
  autoRedeem: boolean;
}

export interface WizardGame {
  gameType: WizardGameType | null;
  mode: 'random' | 'choice';
  outcomes: GameOutcome[];
  noPrize: boolean;
  noPrizeProbability: string;
  winProbability: number;
  /** Spin the Wheel only: one entry per wedge, 4 to 12 (6985:12715). */
  wedges: WizardWedge[];
}

export interface WizardState {
  type: WizardCampaignType | null;
  step: WizardStep;
  objective: WizardObjective | null;
  name: string;
  /** Stamp and Instant Reward use the first; Quest's tasks and Progress's rules are the list. */
  triggers: WizardTrigger[];
  editing: number;
  prizes: StampPrize[];
  levels: ProgressLevel[];
  activeLevel: number;
  game: WizardGame;
  limits: WizardLimits;
}

export const MAX_QUEST_TASKS = 10;

export const MIN_WEDGES = 4;
export const MAX_WEDGES = 12;
/** The wedge count the frames start from. */
export const DEFAULT_WEDGES = 8;

/** The module's own card-length cap (`StampCampaign` `MAX_TOTAL_STAMPS`). */
export const MAX_STAMP_SLOTS = 20;

export const DEFAULT_STAMP_SLOT = 8;

export const TRIGGER_KINDS: TriggerKind[] = [
  'transaction',
  'loyalty',
  'milestone',
  'reward',
  'others'
];

export const CAMPAIGN_ACTIVITIES: CampaignActivity[] = [
  'game_redeemed',
  'game_try_issued',
  'campaign_enrolled',
  'campaign_completed',
  'campaign_not_completed',
  'quest_task_completed'
];

export const WEEK_DAYS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];

export const MONTHS = [
  'jan',
  'feb',
  'mar',
  'apr',
  'may',
  'jun',
  'jul',
  'aug',
  'sep',
  'oct',
  'nov',
  'dec'
];

export const LIMIT_PERIODS: LimitPeriod[] = ['day', 'week', 'month', 'year'];

export const newTrigger = (kind: TriggerKind): WizardTrigger => ({
  kind,
  amountOn: true,
  amount: '',
  typeOn: false,
  transactionType: null,
  scheduleOn: false,
  days: [],
  months: [],
  deadline: null,
  propertiesOn: false,
  properties: [{ key: '', operator: 'equals', value: '' }],
  loyalty: null,
  activity: 'game_redeemed',
  campaign: null,
  rewardActivity: 'any',
  reward: null,
  appTrigger: null,
  earnType: 'fixed',
  points: '',
  base: '1',
  cap: ''
});

export const newPrize = (): WizardPrize => ({
  type: 'points',
  points: '',
  loyalty: null,
  reward: null
});

export const newOutcome = (): GameOutcome => ({ ...newPrize(), probability: '' });

export const newWedge = (): WizardWedge => ({ ...newPrize(), type: 'points' });

/** Resizes the wheel, keeping the wedges that stay. */
export const resizeWedges = (wedges: WizardWedge[], count: number): WizardWedge[] =>
  Array.from({ length: count }, (_, index) => wedges[index] ?? newWedge());

const initialLimits = (): WizardLimits => ({
  totalOn: false,
  total: '',
  perUserOn: false,
  perUser: '',
  rateOn: false,
  rate: '',
  ratePeriod: 'day',
  userRateOn: false,
  userRate: '',
  userRatePeriod: 'day',
  autoEnrol: true,
  enrolByOn: false,
  enrolBy: null,
  windowOn: false,
  windowDays: '',
  start: null,
  endOn: false,
  end: null,
  autoIssue: true,
  autoRedeem: true
});

export const initialWizardState = (): WizardState => ({
  type: null,
  step: 'type',
  objective: null,
  name: '',
  triggers: [],
  editing: 0,
  prizes: [],
  levels: [],
  activeLevel: 0,
  game: {
    gameType: null,
    mode: 'random',
    outcomes: [newOutcome()],
    noPrize: false,
    noPrizeProbability: '',
    winProbability: 50,
    wedges: resizeWedges([], DEFAULT_WEDGES)
  },
  limits: initialLimits()
});

/** Picking a type starts its answers afresh; re-picking the same type keeps them. */
export const chooseType = (state: WizardState, type: WizardCampaignType): WizardState => {
  if (state.type === type) {
    return { ...state, step: 'objective' };
  }
  const fresh = initialWizardState();
  return {
    ...fresh,
    type,
    step: 'objective',
    triggers: type === 'progress' ? [newTrigger('transaction')] : [],
    prizes: [{ ...newPrize(), slot: DEFAULT_STAMP_SLOT }],
    levels: type === 'progress' ? [{ points: '', prizes: [newPrize()] }] : [],
    limits: { ...fresh.limits, autoEnrol: type !== 'quest' && type !== 'progress' }
  };
};

/** The action tile sets the first trigger's kind; a different kind starts that trigger afresh. */
export const chooseAction = (state: WizardState, kind: TriggerKind): WizardState => {
  const current = state.triggers[0];
  const first = current?.kind === kind ? current : newTrigger(kind);
  return { ...state, triggers: [first, ...state.triggers.slice(1)], editing: 0, step: 'trigger' };
};

const listStep = (type: WizardCampaignType | null): WizardStep =>
  type === 'quest' ? 'tasks' : type === 'progress' ? 'rules' : 'prizes';

export const nextStep = (state: WizardState): WizardStep => {
  switch (state.step) {
    case 'type':
      return 'objective';
    case 'objective':
      if (state.type === 'progress') return 'milestones';
      if (state.type === 'game') return 'game';
      return 'action';
    case 'action':
      return 'trigger';
    case 'milestones':
      return state.triggers.length > 1 ? 'rules' : 'trigger';
    case 'trigger':
      return listStep(state.type);
    case 'tasks':
    case 'rules':
      return 'prizes';
    case 'game':
      return state.game.gameType === 'spin_the_wheel' ? 'wedges' : 'outcomes';
    case 'wedges':
      return 'wedgeOutcomes';
    case 'wedgeOutcomes':
      return 'limits';
    case 'outcomes':
      return 'probability';
    case 'probability':
    case 'prizes':
      return 'limits';
    default:
      return 'review';
  }
};

export const previousStep = (state: WizardState): WizardStep => {
  switch (state.step) {
    case 'objective':
      return 'type';
    case 'action':
    case 'milestones':
    case 'game':
      return 'objective';
    case 'trigger':
      if (state.editing > 0) return listStep(state.type);
      return state.type === 'progress' ? 'milestones' : 'action';
    case 'tasks':
      return 'action';
    case 'rules':
      return 'milestones';
    case 'outcomes':
    case 'wedges':
      return 'game';
    case 'wedgeOutcomes':
      return 'wedges';
    case 'probability':
      return 'outcomes';
    case 'prizes':
      return state.type === 'quest' || state.type === 'progress' ? listStep(state.type) : 'trigger';
    case 'limits':
      if (state.type !== 'game') return 'prizes';
      return state.game.gameType === 'spin_the_wheel' ? 'wedgeOutcomes' : 'probability';
    case 'review':
      return 'limits';
    default:
      return 'type';
  }
};

/** "Step n/5" per step; the type picker lights one segment without a number, the review none. */
export const STEP_NUMBER: Record<WizardStep, number | null> = {
  type: null,
  objective: 1,
  action: 2,
  milestones: 2,
  game: 2,
  trigger: 3,
  wedges: 3,
  wedgeOutcomes: 4,
  tasks: 3,
  rules: 3,
  outcomes: 3,
  prizes: 4,
  probability: 4,
  limits: 5,
  review: null
};

/** What every step receives from the wizard. */
export interface IWizardStepProps {
  state: WizardState;
  update: (change: (state: WizardState) => WizardState) => void;
  next: () => void;
  back: () => void;
}

/** Moves an item of a list; positions outside it leave the list as it was. */
export const moveItem = <T>(list: T[], from: number, to: number): T[] => {
  if (from === to || from < 0 || to < 0 || from >= list.length || to >= list.length) {
    return list;
  }
  const moved = [...list];
  const [item] = moved.splice(from, 1);
  moved.splice(to, 0, item);
  return moved;
};

/** Appends a Quest task or Progress rule of `kind` and opens it in the trigger form. */
export const addTrigger = (state: WizardState, kind: TriggerKind): WizardState => ({
  ...state,
  triggers: [...state.triggers, newTrigger(kind)],
  editing: state.triggers.length,
  step: 'trigger'
});

export const removeTrigger = (state: WizardState, index: number): WizardState => ({
  ...state,
  triggers: state.triggers.filter((_, at) => at !== index),
  editing: 0
});

/** The steps the user walks for these answers, first to last, without the list detours. */
export const stepPath = (state: WizardState): WizardStep[] => {
  const path: WizardStep[] = [];
  let walker: WizardState = { ...state, step: 'type', editing: 0 };
  while (path.length < 16) {
    path.push(walker.step);
    if (walker.step === 'review') break;
    walker = { ...walker, step: nextStep(walker) };
  }
  return path;
};

/** Back to the first step of "Step n/5", with every answer kept. */
export const jumpToStep = (state: WizardState, number: number): WizardState => {
  const target = stepPath(state).find(step => STEP_NUMBER[step] === number);
  return target === undefined ? state : { ...state, step: target, editing: 0 };
};

/** A review section's way back to the step (and list item or level tab) that answered it. */
export interface WizardEditTarget {
  step: WizardStep;
  editing?: number;
  activeLevel?: number;
}

export const editAt = (state: WizardState, target: WizardEditTarget): WizardState => ({
  ...state,
  step: target.step,
  editing: target.editing ?? 0,
  activeLevel: target.activeLevel ?? state.activeLevel
});
